import { Card } from '@care/react-component-lib';
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Link,
  Typography,
  Theme,
  Grid,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  mapStateCodeToFullName,
  mapCostGuidePercentageRelativePositionToString,
} from '@/constants';
import { UsStateCode } from '@/__generated__/globalTypes';
import {
  costGuideComparisons_costGuideComparisons_CostGuideComparisonsSuccess_costGuideComparisons_geographicRateComparison as GeographicRateComparisonType,
  costGuideComparisons_costGuideComparisons_CostGuideComparisonsSuccess_costGuideComparisons_mostAffordableRateComparison as MostAffordableRateComparisonType,
  costGuideComparisons_costGuideComparisons_CostGuideComparisonsSuccess_costGuideComparisons_mostExpensiveRateComparison as MostExpensiveRateComparisonType,
  costGuideComparisons_costGuideComparisons_CostGuideComparisonsSuccess_costGuideComparisons_geographicRateComparison_locationComparisons as CostGuideLocationComparisonType,
  costGuideComparisons_costGuideComparisons_CostGuideComparisonsSuccess_costGuideComparisons_mostAffordableRateComparison_locationComparisons_location as CostGuideLocationType,
} from '@/__generated__/CostGuideComparisons';
import ComparisonChart from './ComparisonChart';

interface ComparisonTableProps {
  tableData:
    | GeographicRateComparisonType
    | MostAffordableRateComparisonType
    | MostExpensiveRateComparisonType
    | null;
  currentCity: string | null;
  currentStateCode: UsStateCode | null;
  serviceNameLowercase: string;
  serviceNamePluralLowercase: string;
  isGeographicComparison?: boolean;
  isMostAffordableCities?: boolean;
}

const ComparisonTable = ({
  tableData,
  currentCity,
  currentStateCode,
  serviceNameLowercase,
  serviceNamePluralLowercase,
  isGeographicComparison = false,
  isMostAffordableCities = false,
}: ComparisonTableProps) => {
  const isMobileOrSmaller = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const locationHeader = isGeographicComparison ? 'Location' : 'City';
  const avgHourlyRateHeader = 'Starting rate (avg)';
  const maxHourlyRateHeader = 'Max rate (avg)';
  const numOfDecimalPlaces = 2;
  const stateFullName = currentStateCode ? mapStateCodeToFullName[currentStateCode] : null;
  const comparisonChartClassName = (() => {
    if (isGeographicComparison) {
      return 'geographic';
    }
    if (isMostAffordableCities) {
      return 'most-affordable';
    }
    return 'most-expensive';
  })();

  const roundRate = (amount: string | number, numOfDecimals: number = 0) => {
    if (typeof amount === 'string') {
      return parseFloat(amount).toFixed(numOfDecimals);
    }
    return amount.toFixed(numOfDecimals);
  };

  const getStateGeographicData = () => {
    if (!tableData || tableData.__typename !== 'CostGuideGeographicRateComparison') return null;
    const { state } = tableData.hourlyRateComparisons;
    return {
      stateHourlyRate: roundRate(state.comparison.hourlyRate.amount, numOfDecimalPlaces),
      statePercentage: roundRate(state.comparison.percentage.value),
      stateRelativePosition:
        mapCostGuidePercentageRelativePositionToString[
          state.comparison.percentage.relativePosition
        ],
    };
  };

  const getNationalGeographicData = () => {
    if (!tableData || tableData.__typename !== 'CostGuideGeographicRateComparison') return null;
    const { national } = tableData.hourlyRateComparisons;
    return {
      nationalHourlyRate: roundRate(national.comparison.hourlyRate.amount, numOfDecimalPlaces),
      nationalPercentage: roundRate(national.comparison.percentage.value),
      nationalRelativePosition:
        mapCostGuidePercentageRelativePositionToString[
          national.comparison.percentage.relativePosition
        ],
    };
  };

  const getServiceName = () => {
    if (
      serviceNameLowercase.includes('child care') ||
      serviceNameLowercase.includes('senior care') ||
      serviceNameLowercase.includes('pet care') ||
      serviceNameLowercase.includes('housekeeping') ||
      serviceNameLowercase.includes('house cleaning')
    ) {
      return serviceNamePluralLowercase;
    }

    return `a ${serviceNameLowercase}`;
  };

  const serviceName = getServiceName();
  const stateGeographicData = getStateGeographicData();
  const nationalGeographicData = getNationalGeographicData();

  const headline = () => {
    if (isGeographicComparison) {
      return (
        <Typography variant="h2" mb={1}>
          {`Here's a comparison of ${serviceNameLowercase} costs in ${currentCity}, ${currentStateCode} to both state and national rates`}
        </Typography>
      );
    }

    return (
      <Typography variant="h2" mb={1}>
        <span>{`These are the${' '}`}</span>
        <Typography
          component="span"
          color={isMostAffordableCities ? 'care.green.700' : 'care.red.400'}
          fontWeight="700"
          fontSize="inherit">
          {isMostAffordableCities ? 'most affordable' : 'most expensive'}
        </Typography>
        <span>{`${' '}cities for ${serviceNamePluralLowercase} near ${currentCity}, ${currentStateCode}`}</span>
      </Typography>
    );
  };

  const subHeadline = () => {
    if (isGeographicComparison && stateGeographicData && nationalGeographicData) {
      const { stateHourlyRate, statePercentage, stateRelativePosition } = stateGeographicData;
      const { nationalHourlyRate, nationalPercentage, nationalRelativePosition } =
        nationalGeographicData;

      return (
        <Typography variant={isMobileOrSmaller ? 'body3' : 'body1'} mb={2}>
          <span>{`The cost of hiring ${serviceName} in ${currentCity}, ${currentStateCode} is approximately${' '}`}</span>
          <Typography
            component="span"
            fontWeight="700"
            fontSize="inherit">{`${statePercentage}% ${stateRelativePosition}${' '}`}</Typography>
          <span>{`than ${stateFullName} average starting rate of $${stateHourlyRate} per hour and${' '}`}</span>
          <Typography
            component="span"
            fontWeight="700"
            fontSize="inherit">{`${nationalPercentage}% ${nationalRelativePosition}`}</Typography>
          <span>{`${' '}than the national average of $${nationalHourlyRate}.`}</span>
        </Typography>
      );
    }

    if (isMostAffordableCities) {
      return (
        <Typography variant={isMobileOrSmaller ? 'body3' : 'body1'} mb={2}>
          <span>
            {`In some cases, nearby cities may offer more affordable ${serviceNameLowercase} services. When evaluating
            ${serviceNameLowercase} pay rates in ${currentCity}, ${currentStateCode} and its surrounding areas, it is essential to
            follow the minimum wage regulations and understand the proper way to pay legally. There
            are multiple benefits of paying legally that should be considered, such as tax deductions and fostering a professional
            employment relationship.
            `}
          </span>
        </Typography>
      );
    }

    return (
      <Typography variant={isMobileOrSmaller ? 'body3' : 'body1'} mb={2}>
        {`You may pay more per hour for ${serviceName} near ${currentCity}, ${currentStateCode} 
        and several factors can contribute to driving up the average rates. They include: higher cost of living, increased demand for ${serviceNameLowercase} services, a more competitive job market, and specific details surrounding your care needs (i.e. tasks, timeframe, frequency).
         As a result, it's important to consider these local factors when comparing ${serviceNameLowercase} rates and hiring ${serviceName} near ${currentCity}, ${currentStateCode}.`}
      </Typography>
    );
  };

  const getLocation = (location: CostGuideLocationType | null) => {
    if (!location) return 'United States';

    const { city, state } = location;
    if (city && state.stateCode) {
      return `${city}, ${state.stateCode}`;
    }

    return mapStateCodeToFullName[state.stateCode];
  };

  const tableHeader = () => {
    const headers = [locationHeader, avgHourlyRateHeader, maxHourlyRateHeader].map(
      (header: string) => {
        return (
          <TableCell
            key={header}
            sx={{
              borderBottom: 'none',
              p: 1,
            }}>
            <Typography
              className={comparisonChartClassName}
              variant={isMobileOrSmaller ? 'info1' : 'h3'}
              fontWeight="700">
              {header}
            </Typography>
          </TableCell>
        );
      }
    );
    return <TableRow>{headers}</TableRow>;
  };

  const tableRowData = (rowData: CostGuideLocationComparisonType): JSX.Element => {
    const { location, rates, costPageUrl } = rowData;
    return (
      <TableRow
        key={costPageUrl}
        sx={{
          '& .MuiTableCell-root': {
            borderBottom: 'none',
            p: 1,
            minWidth: { sm: '140px' },
          },
        }}>
        <TableCell>
          {location?.city && costPageUrl && (
            <Link href={costPageUrl} variant={isMobileOrSmaller ? 'body3' : 'body2'}>
              {getLocation(location)}
            </Link>
          )}
          {!location?.city && (
            <Typography variant={isMobileOrSmaller ? 'body3' : 'body2'}>
              {getLocation(location)}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          {rates?.averageRate?.averageHourlyRate?.rate?.amount && (
            <Typography variant={isMobileOrSmaller ? 'body3' : 'body2'}>{`$${roundRate(
              rates.averageRate.averageHourlyRate.rate.amount,
              numOfDecimalPlaces
            )}/hr`}</Typography>
          )}
        </TableCell>
        <TableCell>
          {rates?.maximumRate?.maximumHourlyRate?.rate?.amount && (
            <Typography variant={isMobileOrSmaller ? 'body3' : 'body2'}>{`$${roundRate(
              rates.maximumRate.maximumHourlyRate.rate.amount,
              numOfDecimalPlaces
            )}/hr`}</Typography>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const tableBodyData = (): JSX.Element[] => {
    if (!tableData) return [];
    return tableData.locationComparisons.map((rowData: CostGuideLocationComparisonType) => {
      return tableRowData(rowData);
    });
  };

  return (
    <Box p={{ xs: 3, sm: 5 }}>
      {headline()}
      {subHeadline()}
      <Card
        elevation={0}
        className={comparisonChartClassName}
        sx={{
          border: 'none',
          py: { xs: 2, sm: 4 },
          px: { xs: 2, sm: 6 },

          '&.geographic': {
            backgroundColor: 'background.blue',
          },
          '&.most-affordable': {
            backgroundColor: 'background.green',
          },
          '&.most-expensive': {
            backgroundColor: 'background.latte',
          },
        }}>
        <Grid
          container
          item
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', md: 'row' },
          }}>
          <Grid
            className={comparisonChartClassName}
            item
            pb={3}
            sx={{
              '&.geographic': {
                md: 6,
                width: 'auto',
              },
              '&.most-affordable, &.most-expensive': {
                width: '100%',
              },
            }}>
            <Table>
              <TableHead>{tableHeader()}</TableHead>
              <TableBody>{tableBodyData()}</TableBody>
            </Table>
          </Grid>
          {isGeographicComparison && tableData && (
            <Grid
              className={comparisonChartClassName}
              item
              sx={{
                '&.geographic': {
                  md: 6,
                },
              }}>
              <ComparisonChart chartData={tableData.locationComparisons} />
            </Grid>
          )}
        </Grid>
      </Card>
    </Box>
  );
};

export default ComparisonTable;
